import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import SEO from '../../components/SEO';
import { 
  BookOpen, TrendingUp, Shield, Target, ArrowRight, 
  ChevronRight, Zap, BarChart3, AlertTriangle, Clock
} from 'lucide-react';

// Rehber içerikleri - yeni eklendikçe buraya
const rehberler = [
  {
    slug: 'kripto-sinyali-nedir',
    title: 'Kripto Sinyali Nedir?',
    description: 'TP, Stop Loss, LONG ve SHORT kavramlarını basitçe öğrenin. Trading sinyalleri nasıl çalışır?',
    icon: <Zap className="h-6 w-6" />,
    color: 'purple',
    readTime: '5 dk',
    category: 'Temel'
  },
  {
    slug: 'tp-ve-stop-loss-nedir',
    title: 'TP ve Stop Loss Nedir?',
    description: 'Take Profit ve Stop Loss kavramlarını örneklerle anlayın. Risk yönetiminin temelleri.',
    icon: <Target className="h-6 w-6" />,
    color: 'green',
    readTime: '4 dk',
    category: 'Temel'
  },
  {
    slug: 'long-ve-short-ne-demek',
    title: 'LONG ve SHORT Ne Demek?',
    description: 'Yükseliş ve düşüş yönlü pozisyonları anlayın. Hangi durumda hangi yön?',
    icon: <TrendingUp className="h-6 w-6" />,
    color: 'blue',
    readTime: '4 dk',
    category: 'Temel'
  },
  {
    slug: 'kripto-sinyali-nasil-okunur',
    title: 'Kripto Sinyali Nasıl Okunur?',
    description: 'Bir sinyal kartını adım adım okumayı öğrenin. Giriş, TP, Stop seviyelerini anlama.',
    icon: <BarChart3 className="h-6 w-6" />,
    color: 'amber',
    readTime: '6 dk',
    category: 'Pratik'
  },
  {
    slug: 'yeni-baslayanlar-icin-risk-yonetimi',
    title: 'Yeni Başlayanlar İçin Risk Yönetimi',
    description: 'Pozisyon boyutu, risk/ödül oranı ve sermaye koruma stratejileri.',
    icon: <Shield className="h-6 w-6" />,
    color: 'red',
    readTime: '7 dk',
    category: 'İleri'
  },
  {
    slug: 'rsi-nedir',
    title: 'RSI Nedir?',
    description: 'RSI (Relative Strength Index) nasıl hesaplanır ve yorumlanır? Aşırı alım-satım bölgeleri ve divergence.',
    icon: <BarChart3 className="h-6 w-6" />,
    color: 'blue',
    readTime: '8 dk',
    category: 'Teknik Analiz'
  },
  {
    slug: 'macd-nedir',
    title: 'MACD Nedir?',
    description: 'MACD göstergesi, sinyal çizgisi ve histogram nasıl yorumlanır? Crossover stratejileri.',
    icon: <TrendingUp className="h-6 w-6" />,
    color: 'purple',
    readTime: '7 dk',
    category: 'Teknik Analiz'
  },
  {
    slug: 'fibonacci-nedir',
    title: 'Fibonacci Nedir?',
    description: 'Fibonacci retracement ve extension seviyeleri. %61.8 altın oran ve geri çekilme stratejileri.',
    icon: <Target className="h-6 w-6" />,
    color: 'amber',
    readTime: '8 dk',
    category: 'Teknik Analiz'
  },
  {
    slug: 'destek-direnc-nedir',
    title: 'Destek ve Direnç Nedir?',
    description: 'Support ve resistance seviyeleri nasıl belirlenir? Rol değişimi ve kırılma stratejileri.',
    icon: <Shield className="h-6 w-6" />,
    color: 'green',
    readTime: '7 dk',
    category: 'Teknik Analiz'
  },
  {
    slug: 'long-nedir',
    title: 'Long Nedir?',
    description: 'Long pozisyon nedir, nasıl çalışır? Spot ve futures piyasada long açma mantığı ve kaldıraç.',
    icon: <TrendingUp className="h-6 w-6" />,
    color: 'green',
    readTime: '8 dk',
    category: 'Trading'
  },
  {
    slug: 'short-nedir',
    title: 'Short Nedir?',
    description: 'Short pozisyon nedir? Düşüş trendlerinden kazanç, short squeeze ve risk yönetimi.',
    icon: <TrendingUp className="h-6 w-6" style={{ transform: 'rotate(180deg)' }} />,
    color: 'red',
    readTime: '8 dk',
    category: 'Trading'
  },
  {
    slug: 'futures-trading-nedir',
    title: 'Futures Trading Nedir?',
    description: 'Kripto vadeli işlemler rehberi. Kaldıraç, likidasyon, long/short ve risk yönetimi.',
    icon: <BarChart3 className="h-6 w-6" />,
    color: 'purple',
    readTime: '10 dk',
    category: 'Trading'
  }
];

const colorClasses = {
  purple: 'border-purple-500/20 bg-purple-500/10 text-purple-400 hover:border-purple-500/40',
  green: 'border-green-500/20 bg-green-500/10 text-green-400 hover:border-green-500/40',
  blue: 'border-blue-500/20 bg-blue-500/10 text-blue-400 hover:border-blue-500/40',
  amber: 'border-amber-500/20 bg-amber-500/10 text-amber-400 hover:border-amber-500/40',
  red: 'border-red-500/20 bg-red-500/10 text-red-400 hover:border-red-500/40'
};

const RehberPage = () => {
  return (
    <div className="min-h-screen bg-brand-dark">
      <SEO 
        title="Rehber & Eğitim Merkezi | Borsamex"
        description="Kripto trading sinyalleri hakkında her şeyi öğrenin. TP, Stop Loss, LONG, SHORT kavramları ve risk yönetimi rehberleri."
        keywords="kripto rehber, trading eğitim, kripto sinyali nedir, tp nedir, stop loss nedir, long short nedir, trading öğren"
        canonical="https://borsamex.com/rehber"
      />

      {/* Hero */}
      <section className="py-16 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-500/5 via-transparent to-transparent" />
        <div className="container mx-auto px-4 max-w-5xl relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <Badge className="mb-6 px-4 py-2 bg-purple-500/10 text-purple-400 border-purple-500/20">
              <BookOpen className="h-4 w-4 mr-2" />
              Eğitim Merkezi
            </Badge>
            
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-white mb-6">
              Rehber & Eğitim
            </h1>
            
            <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
              Kripto trading sinyallerini anlamak için ihtiyacın olan her şey. 
              Temel kavramlardan ileri stratejilere.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Rehber Listesi */}
      <section className="py-12 pb-20">
        <div className="container mx-auto px-4 max-w-5xl">
          {/* Kategoriler */}
          <div className="flex flex-wrap gap-3 mb-10 justify-center">
            <Badge className="px-4 py-2 bg-slate-800 text-white border-slate-700">Tümü</Badge>
            <Badge className="px-4 py-2 bg-slate-900 text-slate-400 border-slate-700 hover:bg-slate-800 cursor-pointer">Temel</Badge>
            <Badge className="px-4 py-2 bg-slate-900 text-slate-400 border-slate-700 hover:bg-slate-800 cursor-pointer">Pratik</Badge>
            <Badge className="px-4 py-2 bg-slate-900 text-slate-400 border-slate-700 hover:bg-slate-800 cursor-pointer">İleri</Badge>
            <Badge className="px-4 py-2 bg-slate-900 text-slate-400 border-slate-700 hover:bg-slate-800 cursor-pointer">Teknik Analiz</Badge>
            <Badge className="px-4 py-2 bg-slate-900 text-slate-400 border-slate-700 hover:bg-slate-800 cursor-pointer">Trading</Badge>
          </div>

          {/* Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rehberler.map((rehber, idx) => (
              <motion.div
                key={rehber.slug}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                {rehber.comingSoon ? (
                  <Card className="h-full border-slate-700/50 bg-slate-900/30 opacity-60">
                    <CardContent className="p-6">
                      <div className={`w-12 h-12 rounded-xl ${colorClasses[rehber.color].split(' ').slice(1, 3).join(' ')} flex items-center justify-center mb-4`}>
                        {rehber.icon}
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className="bg-slate-800 text-slate-500 border-slate-700 text-xs">{rehber.category}</Badge>
                        <Badge className="bg-amber-900/30 text-amber-500 border-amber-700/30 text-xs">Yakında</Badge>
                      </div>
                      <h3 className="text-lg font-bold text-slate-400 mb-2">{rehber.title}</h3>
                      <p className="text-slate-500 text-sm mb-4">{rehber.description}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-600">
                        <Clock className="h-3 w-3" />
                        <span>{rehber.readTime} okuma</span>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Link to={`/rehber/${rehber.slug}`} className="block group h-full">
                    <Card className={`h-full border-slate-700/50 bg-slate-900/50 hover:bg-slate-800/50 transition-all ${colorClasses[rehber.color].split(' ')[0]} hover:${colorClasses[rehber.color].split(' ').pop()}`}>
                      <CardContent className="p-6">
                        <div className={`w-12 h-12 rounded-xl ${colorClasses[rehber.color].split(' ').slice(1, 3).join(' ')} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                          {rehber.icon}
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-slate-800 text-slate-400 border-slate-700 text-xs">{rehber.category}</Badge>
                        </div>
                        <h3 className="text-lg font-bold text-white mb-2 group-hover:text-purple-400 transition-colors">{rehber.title}</h3>
                        <p className="text-slate-400 text-sm mb-4">{rehber.description}</p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <Clock className="h-3 w-3" />
                            <span>{rehber.readTime} okuma</span>
                          </div>
                          <span className="text-purple-400 text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                            Oku <ChevronRight className="h-4 w-4" />
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-900/50">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            Öğrendiklerini Uygulamaya Hazır mısın?
          </h2>
          <p className="text-slate-400 mb-8">
            Ücretsiz kaydol ve canlı sinyalleri takip etmeye başla.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-brand-green hover:bg-brand-green/90 text-brand-dark font-bold px-8">
              Ücretsiz Başla <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default RehberPage;
